// No Bootstrap JS imports — Popover API handles the dropdown,
// <dialog> handles the modals, native title handles tooltips.
import { when } from 'mobx';
import { fetchUserGedcomFiles, loadGedcomFile } from './gedcomFileHandler.js';
import gedcomModalManager from './gedcomModalUtils.js';
import authStore from '../common/stores/authStore.js';
import notificationManager from '../utils/NotificationManager.js';
import { filesService } from '../services/filesService.js';
import { logger } from '../utils/debug/logger.js';

const EMAIL_REGEX = /^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const MAX_EMAIL_ROWS = 5;

class GedcomDropdownManager {
  _filesCache = null;
  _isLoading = false;
  _isInitialized = false;
  _shareModal = null;
  _proposeModal = null;

  /**
   * Initialize dropdown event listeners.
   * Called once from eventListeners.js after DOM is ready.
   */
  initialize() {
    if (this._isInitialized) return;
    this._isInitialized = true;

    const toggleEl = document.getElementById('importMenu');
    const menuEl = document.getElementById('gedcomDropdownMenu');
    if (!toggleEl || !menuEl) return;

    // Position the popover under the toggler (left-aligned). The Popover
    // API handles visibility / outside-close / ESC, we only own anchoring.
    const place = () => {
      const r = toggleEl.getBoundingClientRect();
      menuEl.style.top = `${r.bottom + 4}px`;
      menuEl.style.left = `${r.left}px`;
    };
    menuEl.addEventListener('beforetoggle', (e) => {
      if (e.newState === 'open') {
        place();
        this._onDropdownShow();
      }
    });
    menuEl.addEventListener('toggle', (e) => {
      toggleEl.setAttribute('aria-expanded', e.newState === 'open' ? 'true' : 'false');
    });
    window.addEventListener('resize', () => {
      if (menuEl.matches(':popover-open')) place();
    });

    // Event delegation on the popover content
    menuEl.addEventListener('click', (e) => this._onMenuClick(e));

    // Close popover when a file is selected via file input
    const fileInput = document.getElementById('file');
    if (fileInput) {
      fileInput.addEventListener('change', () => this._closeDropdown());
    }

    // Close popover when the sample link is clicked
    const sampleLink = document.getElementById('gedcomFileSample');
    if (sampleLink) {
      sampleLink.addEventListener('click', () => this._closeDropdown());
    }

    // Prefetch the file list as soon as the user is authenticated, so the
    // cache is already warm by the time they click on GEDCOM. when() fires
    // exactly once and unregisters itself.
    when(
      () => authStore.isAuthenticated,
      () => { this._loadFiles({ silent: true }).catch(() => {}); },
    );
  }

  _closeDropdown() {
    const menuEl = document.getElementById('gedcomDropdownMenu');
    if (menuEl?.matches(':popover-open')) menuEl.hidePopover();
  }

  async _onDropdownShow() {
    if (!authStore.isAuthenticated) {
      this._hideManageSection();
      return;
    }

    // If the prefetch already populated the cache, render synchronously
    // and refresh in the background (stale-while-revalidate). Otherwise
    // show the spinner while we wait for the first response.
    if (this._filesCache) {
      const menuEl = document.getElementById('gedcomDropdownMenu');
      if (this._filesCache.length === 0) {
        this._hideManageSection();
      } else {
        this._showManageSection();
        if (menuEl) {
          menuEl.querySelectorAll('.gedcom-file-entry').forEach(el => el.remove());
          this._renderFiles(this._filesCache, menuEl);
        }
      }
      // Background refresh — don't block render, don't show spinner.
      this._loadFiles({ silent: true }).catch(() => {});
      return;
    }

    await this._loadFiles();
  }

  _showManageSection() {
    document.querySelectorAll('.gedcom-manage-section').forEach(el => {
      // Don't touch loading/empty indicators — they are managed by _loadFiles()
      if (el.id === 'gedcom-files-loading' || el.id === 'gedcom-files-empty') return;
      el.style.display = '';
    });
  }

  _hideManageSection() {
    document.querySelectorAll('.gedcom-manage-section').forEach(el => {
      el.style.display = 'none';
    });
    // Dispose tooltips and clear any rendered file entries
    const listEl = document.getElementById('gedcomDropdownMenu');
    if (listEl) {
      this._disposeTooltips(listEl);
      listEl.querySelectorAll('.gedcom-file-entry').forEach(el => el.remove());
    }
  }

  async _loadFiles({ silent = false } = {}) {
    if (this._isLoading) return;
    this._isLoading = true;

    const loadingEl = document.getElementById('gedcom-files-loading');
    const emptyEl = document.getElementById('gedcom-files-empty');
    const menuEl = document.getElementById('gedcomDropdownMenu');

    if (emptyEl) emptyEl.style.display = 'none';

    if (silent) {
      // Background refresh: don't flash the spinner and don't tear
      // down the existing list — let _renderFiles below replace it.
      if (loadingEl) loadingEl.style.display = 'none';
    } else {
      // Foreground load (cold cache): clear any stale entries and show
      // the spinner so the user knows the second half of the dropdown
      // is on its way.
      if (menuEl) {
        menuEl.querySelectorAll('.gedcom-file-entry').forEach(el => el.remove());
      }
      this._showManageSection();
      // The static "Gérer" header is replaced by the per-group sub-headers
      // injected by _renderFiles; keep it hidden during the spinner phase
      // so the user doesn't see a flash of the wrong label.
      const staticManageHeader = document.getElementById('gedcom-manage-header');
      if (staticManageHeader) staticManageHeader.style.display = 'none';
      if (loadingEl) loadingEl.style.display = '';
    }

    try {
      const files = await fetchUserGedcomFiles();
      this._filesCache = files;

      if (loadingEl) loadingEl.style.display = 'none';

      if (files.length === 0) {
        this._hideManageSection();
        return;
      }

      // Re-render every time so background refreshes pick up server-side
      // changes (new uploads, deletions, merge results).
      if (menuEl) {
        menuEl.querySelectorAll('.gedcom-file-entry').forEach(el => el.remove());
      }
      this._showManageSection();
      this._renderFiles(files, menuEl);
    } catch (error) {
      logger.gedcom.error('Error loading files for dropdown:', error);
      if (loadingEl) loadingEl.style.display = 'none';
      if (!silent) this._hideManageSection();
    } finally {
      this._isLoading = false;
    }
  }

  _renderFiles(files, menuEl) {
    if (!menuEl) return;

    // Used to resolve the contributor file name in the 'enrichi' badge
    // tooltip — built once per render.
    const byId = new Map(files.map((f) => [f.id, f]));

    // Split by ownership so we can label each group. Sub-headers
    // replace the generic "Gérer" entry — that one is hidden below.
    const owned = files.filter(f => f.status !== 'shared');
    const shared = files.filter(f => f.status === 'shared');

    const staticManageHeader = document.getElementById('gedcom-manage-header');
    if (staticManageHeader) staticManageHeader.style.display = 'none';

    if (owned.length > 0) {
      menuEl.appendChild(this._renderSubHeader('Mes fichiers'));
      owned.forEach(file => this._renderFileEntry(file, menuEl, byId));
    }
    if (shared.length > 0) {
      menuEl.appendChild(this._renderSubHeader('Partagés avec moi'));
      shared.forEach(file => this._renderFileEntry(file, menuEl, byId));
    }

    // Native `title` attributes are used instead of Bootstrap Tooltip
    // inside the popover — BS tooltips render in the DOM (z-index ~1080)
    // while the popover lives in the browser's top-layer, so BS tooltips
    // would be hidden behind it.
  }

  _renderSubHeader(label) {
    const li = document.createElement('li');
    // gedcom-file-entry so the existing cleanup in _loadFiles / _hideManageSection removes it on re-render.
    li.className = 'gedcom-manage-section gedcom-file-entry gedcom-subsection-header';
    const span = document.createElement('span');
    span.className = 'dropdown-item-text gedcom-subsection-label';
    span.textContent = label;
    li.appendChild(span);
    return li;
  }

  _renderFileEntry(file, menuEl, byId) {
      const displayName = file.familyName
        || gedcomModalManager.extractFamilyNameFromFilename(file.name);
      const li = document.createElement('li');
      li.className = 'gedcom-manage-section gedcom-file-entry';
      li.dataset.fileId = file.id;

      // Build actions (only share+propose+delete for owned files)
      // Use inline SVGs instead of Bootstrap Icons font classes for reverse proxy compatibility
      let actionsHtml = '';
      if (file.status === 'owned') {
        actionsHtml = `
          <span class="gedcom-file-actions">
            <a href="#" class="gedcom-action-icon" data-action="share" data-file-id="${file.id}"
               data-tip="Partager">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M13.5 1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.5 2.5 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5m-8.5 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3m11 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3"/>
              </svg>
            </a>
            <a href="#" class="gedcom-action-icon" data-action="propose" data-file-id="${file.id}"
               data-tip="Échanger les données">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path fill-rule="evenodd" d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5m14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5"/>
              </svg>
            </a>
            <a href="#" class="gedcom-action-icon text-danger" data-action="delete" data-file-id="${file.id}"
               data-tip="Supprimer">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0z"/>
                <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4zM2.5 3h11V2h-11z"/>
              </svg>
            </a>
          </span>`;
      }

      const itemDiv = document.createElement('div');
      itemDiv.className = 'dropdown-item d-flex align-items-center justify-content-between gedcom-file-item';

      // Wrap name + chain badge together so the badge survives even when
      // the name itself is truncated by text-overflow on long entries.
      const nameWrapper = document.createElement('span');
      nameWrapper.className = 'd-flex align-items-center gap-2 flex-grow-1 text-truncate';

      const nameSpan = document.createElement('span');
      nameSpan.className = 'gedcom-file-name text-truncate';
      nameSpan.setAttribute('role', 'button');
      nameSpan.dataset.action = 'load';
      nameSpan.dataset.fileId = file.id;
      nameSpan.dataset.link = file.signedUrl || '';
      nameSpan.textContent = displayName;

      // Shared files: show the owner as a subtitle under the name.
      if (file.status === 'shared' && file.owner_id) {
        const nameCol = document.createElement('span');
        nameCol.className = 'gedcom-file-name-col';
        nameCol.appendChild(nameSpan);
        const sub = document.createElement('span');
        sub.className = 'gedcom-file-shared-by';
        sub.textContent = `par ${file.owner_id}`;
        nameCol.appendChild(sub);
        nameWrapper.appendChild(nameCol);
      } else {
        nameWrapper.appendChild(nameSpan);
      }

      // Badge the file's role in its merge chain (if any). A file with
      // children is an "original" that has been superseded by a merge;
      // a file with a parent is a "merged" version. Standalone files
      // (no parent, no children) get no badge. Tooltips quote the
      // counterpart name + date so the user can trace where each
      // version came from.
      const badge = this._renderChainBadge(file, { byId });
      if (badge) nameWrapper.appendChild(badge);

      itemDiv.appendChild(nameWrapper);
      if (actionsHtml) {
        const actionsWrapper = document.createElement('span');
        actionsWrapper.innerHTML = actionsHtml;
        while (actionsWrapper.firstChild) {
          itemDiv.appendChild(actionsWrapper.firstChild);
        }
      }
      li.appendChild(itemDiv);

      menuEl.appendChild(li);
  }

  _renderChainBadge(file, { byId } = {}) {
    // hasChildren wins when both are set (the file is mid-chain — rare).
    // Labels are intentionally short so the row stays scannable.
    let label = null;
    let cls = 'bg-secondary-subtle text-secondary';
    let title = null;
    if (file.hasChildren) {
      label = 'original';
      // No tooltip — the merged row sits right next to this one with
      // its own date + contributor in its own tooltip; quoting it
      // here would just echo the line above.
    } else if (file.parentFileId) {
      label = 'enrichi';
      cls = 'bg-success-subtle text-success';
      // Prefer naming the contributor (the absorbed source) — the
      // parent is the owner's pre-fusion state and is usually already
      // visible right below as the "original" badge.
      const contrib = file.contributorFileId ? byId?.get(file.contributorFileId) : null;
      if (contrib) {
        const contribName = contrib.familyName || gedcomModalManager.extractFamilyNameFromFilename(contrib.name);
        title = `Enrichi le ${this._formatDate(file.uploadedAt)} avec « ${contribName} »`;
      } else {
        // Contributor was deleted or belongs to another user.
        title = `Enrichi le ${this._formatDate(file.uploadedAt)}`;
      }
    }
    if (!label) return null;
    const span = document.createElement('span');
    span.className = `badge ${cls} fw-normal flex-shrink-0`;
    span.style.fontSize = '0.7em';
    span.textContent = label;
    if (title) {
      span.setAttribute('data-tip', title);
    }
    return span;
  }

  _formatDate(iso) {
    if (!iso) return '?';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  async _onMenuClick(e) {
    const actionEl = e.target.closest('[data-action]');
    if (!actionEl) return;

    const action = actionEl.dataset.action;
    const fileId = actionEl.dataset.fileId;

    switch (action) {
      case 'load':
        await this._handleLoad(actionEl);
        break;
      case 'share':
        e.preventDefault();
        e.stopPropagation();
        this._handleShare(fileId);
        break;
      case 'propose':
        e.preventDefault();
        e.stopPropagation();
        this._handlePropose(fileId);
        break;
      case 'delete':
        e.preventDefault();
        e.stopPropagation();
        await this._handleDelete(fileId, actionEl);
        break;
      case 'confirm-delete':
        e.preventDefault();
        e.stopPropagation();
        // Handled by inline listener in _handleDelete
        break;
      case 'cancel-delete':
        e.preventDefault();
        e.stopPropagation();
        // Handled by inline listener in _handleDelete
        break;
    }
  }

  async _handleLoad(el) {
    const fileId = el.dataset.fileId;
    if (!fileId) return;

    this._closeDropdown();

    // files-service issues short-lived signed download URLs on demand
    // (no longer baked into the list response). Fetch a fresh one each
    // click so URLs never go stale between dropdown open and click.
    try {
      const { filesService } = await import('../services/filesService.js');
      const file = await filesService.getFile(fileId);
      if (!file?.downloadUrl) {
        logger.gedcom.error('files-service did not return a downloadUrl for', fileId);
        return;
      }
      // Track the file_id BEFORE loading so downstream features
      // (Photos tab group lookup) bind to the stable id, not the
      // filename which gets transformed during load.
      const configStore = (await import('../tabs/fanChart/fanConfigStore.js')).default;
      configStore.setCurrentFileId(fileId);
      await loadGedcomFile(file.downloadUrl);
    } catch (error) {
      logger.gedcom.error('Erreur lors du load via files-service:', error);
    }
  }

  // =========================================
  // Share — Lightweight modal
  // =========================================

  _handleShare(fileId) {
    if (!authStore.isAuthenticated) return;

    const file = this._filesCache?.find(f => f.id === fileId);
    if (!file) return;

    this._closeDropdown();

    const displayName = file.familyName
      || gedcomModalManager.extractFamilyNameFromFilename(file.name);
    this._openShareModal(fileId, displayName);
  }

  _openShareModal(fileId, displayName) {
    this._cleanupShareModal();

    const dialogEl = this._createShareModal(fileId, displayName);
    document.body.appendChild(dialogEl);

    this._shareModal = dialogEl;

    // Native <dialog> emits 'close' (no Bootstrap event needed)
    dialogEl.addEventListener('close', () => this._cleanupShareModal(), { once: true });

    // showModal() — native modal with backdrop + focus trap + ESC
    dialogEl.showModal();

    // Focus first email input
    requestAnimationFrame(() => {
      dialogEl.querySelector('.gedcom-share-email-input')?.focus();
    });
  }

  _createShareModal(fileId, displayName) {
    const modalEl = document.createElement('dialog');
    modalEl.className = 'app-dialog';
    modalEl.id = 'gedcomShareModal';

    modalEl.innerHTML = `
      <div class="modal-content gedcom-share-modal">
          <div class="modal-header">
            <h6 class="modal-title">Partager &middot; Famille ${this._escapeHtml(displayName)}</h6>
            <button type="button" class="btn-close" data-action="dialog-close" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="gedcom-share-emails" id="shareEmailsContainer"></div>
            <button type="button" class="gedcom-share-add-btn" id="shareAddEmailBtn">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4"/>
              </svg>
              Ajouter un destinataire
            </button>
            <div class="gedcom-share-results" id="shareResults" style="display:none;"></div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary" id="shareSubmitBtn" disabled>
              Partager
              <span class="spinner-border spinner-border-sm ms-2" id="shareSpinner" style="display:none;" role="status"></span>
            </button>
          </div>
      </div>`;

    // Backdrop click closes (native <dialog> doesn't do this by default
    // — only ESC does. We replicate Bootstrap Modal's UX.)
    modalEl.addEventListener('click', (e) => {
      if (e.target === modalEl) modalEl.close();
      else if (e.target.closest('[data-action="dialog-close"]')) modalEl.close();
    });

    const container = modalEl.querySelector('#shareEmailsContainer');
    const addBtn = modalEl.querySelector('#shareAddEmailBtn');
    const submitBtn = modalEl.querySelector('#shareSubmitBtn');

    // Add first email row
    this._addEmailRow(container);

    // Add email button
    addBtn.addEventListener('click', () => {
      if (container.children.length < MAX_EMAIL_ROWS) {
        this._addEmailRow(container);
        // Focus the new input
        container.lastElementChild.querySelector('input')?.focus();
      }
      // Hide button when max reached
      if (container.children.length >= MAX_EMAIL_ROWS) {
        addBtn.style.display = 'none';
      }
    });

    // Submit
    submitBtn.addEventListener('click', () => {
      this._submitShare(fileId, displayName, modalEl);
    });

    return modalEl;
  }

  _addEmailRow(container) {
    const row = document.createElement('div');
    row.className = 'gedcom-share-email-row';

    const input = document.createElement('input');
    input.type = 'email';
    input.className = 'form-control form-control-sm gedcom-share-email-input';
    input.placeholder = 'adresse@email.com';

    input.addEventListener('input', () => {
      this._updateShareValidation(container);
      const email = input.value.trim();
      if (email === '') {
        input.classList.remove('is-valid', 'is-invalid');
      } else if (EMAIL_REGEX.test(email)) {
        input.classList.remove('is-invalid');
        input.classList.add('is-valid');
      } else {
        input.classList.remove('is-valid');
        input.classList.add('is-invalid');
      }
    });

    // Enter key submits the form
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const submitBtn = document.getElementById('shareSubmitBtn');
        if (submitBtn && !submitBtn.disabled) submitBtn.click();
      }
    });

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'gedcom-share-remove-btn';
    removeBtn.innerHTML = '&times;';
    removeBtn.addEventListener('click', () => {
      row.remove();
      this._updateShareValidation(container);
      // Show add button again
      const addBtn = document.getElementById('shareAddEmailBtn');
      if (addBtn) addBtn.style.display = '';
      // Update remove button visibility
      this._updateRemoveButtons(container);
    });

    row.append(input, removeBtn);
    container.appendChild(row);
    this._updateRemoveButtons(container);
  }

  _updateRemoveButtons(container) {
    const rows = container.querySelectorAll('.gedcom-share-email-row');
    rows.forEach(row => {
      const btn = row.querySelector('.gedcom-share-remove-btn');
      if (btn) btn.style.display = rows.length <= 1 ? 'none' : '';
    });
  }

  _updateShareValidation(container) {
    const inputs = container.querySelectorAll('.gedcom-share-email-input');
    const submitBtn = document.getElementById('shareSubmitBtn');
    if (!submitBtn) return;

    let hasValid = false;
    let hasInvalid = false;

    inputs.forEach(input => {
      const email = input.value.trim();
      if (email === '') return;
      if (EMAIL_REGEX.test(email)) {
        hasValid = true;
      } else {
        hasInvalid = true;
      }
    });

    submitBtn.disabled = !hasValid || hasInvalid;
  }

  async _submitShare(fileId, displayName, modalEl) {
    const container = modalEl.querySelector('#shareEmailsContainer');
    const submitBtn = modalEl.querySelector('#shareSubmitBtn');
    const spinner = modalEl.querySelector('#shareSpinner');
    const resultsEl = modalEl.querySelector('#shareResults');
    const addBtn = modalEl.querySelector('#shareAddEmailBtn');

    // Collect valid emails
    const emails = [];
    container.querySelectorAll('.gedcom-share-email-input').forEach(input => {
      const email = input.value.trim();
      if (EMAIL_REGEX.test(email)) emails.push(email);
    });

    if (emails.length === 0) return;

    // Show spinner, disable UI
    submitBtn.disabled = true;
    spinner.style.display = '';
    container.querySelectorAll('input').forEach(i => i.disabled = true);
    if (addBtn) addBtn.style.display = 'none';

    try {
      // Routes through /files-api/files/:id/share — files-service
      // owns the orchestration now (validate → mint → grant → email
      // → mailchimp). The displayName is read server-side from the
      // file's metadata.familyName, no need to forward it. The owner
      // identity comes from the auth cookie automatically.
      const result = await filesService.shareFile(fileId, { emails });
      this._showShareResults(resultsEl, result, emails);
    } catch (error) {
      logger.gedcom.error('Error sharing file:', error);
      resultsEl.innerHTML = `<div class="gedcom-share-result-item error">Erreur : ${this._escapeHtml(error.message)}</div>`;
      resultsEl.style.display = '';
    } finally {
      spinner.style.display = 'none';
      // Hide form, show only results
      container.style.display = 'none';
      submitBtn.closest('.modal-footer').style.display = 'none';
    }
  }

  _showShareResults(resultsEl, result, emails) {
    const sharingResults = result.sharingResults || [];
    const invitationResults = result.invitationResults || [];

    let html = '';
    emails.forEach(email => {
      const sharing = sharingResults.find(r => r.email === email);
      const invitation = invitationResults.find(r => r.email === email);

      const shareOk = sharing?.status === 'Partage réussi';
      const inviteOk = invitation?.status === 'Invitation envoyée';

      if (shareOk) {
        const inviteLabel = inviteOk ? 'invitation envoyée' : 'partage effectué';
        html += `<div class="gedcom-share-result-item success">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
          </svg>
          ${this._escapeHtml(email)} &mdash; ${inviteLabel}
        </div>`;
      } else {
        const reason = sharing?.error || sharing?.reason || 'erreur inconnue';
        html += `<div class="gedcom-share-result-item error">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293z"/>
          </svg>
          ${this._escapeHtml(email)} &mdash; ${this._escapeHtml(reason)}
        </div>`;
      }
    });

    resultsEl.innerHTML = html;
    resultsEl.style.display = '';
  }

  _cleanupShareModal() {
    document.getElementById('gedcomShareModal')?.remove();
    this._shareModal = null;
  }

  // =========================================
  // Propose (échanger les données) — reciprocal merge proposal
  // =========================================

  _handlePropose(fileId) {
    if (!authStore.isAuthenticated) return;
    const file = this._filesCache?.find(f => f.id === fileId);
    if (!file) return;
    this._closeDropdown();
    const displayName = file.familyName
      || gedcomModalManager.extractFamilyNameFromFilename(file.name);
    this._openProposeModal(fileId, displayName);
  }

  _openProposeModal(fileId, displayName) {
    this._cleanupProposeModal();
    const dialogEl = this._createProposeModal(fileId, displayName);
    document.body.appendChild(dialogEl);
    this._proposeModal = dialogEl;
    dialogEl.addEventListener('close', () => this._cleanupProposeModal(), { once: true });
    dialogEl.showModal();
    requestAnimationFrame(() => {
      dialogEl.querySelector('#proposeEmailInput')?.focus();
    });
  }

  _createProposeModal(fileId, displayName) {
    const modalEl = document.createElement('dialog');
    modalEl.className = 'app-dialog';
    modalEl.id = 'gedcomProposeModal';

    modalEl.innerHTML = `
      <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title">
              <i class="bi bi-arrow-left-right me-2"></i>
              Proposer un échange &middot; Famille ${this._escapeHtml(displayName)}
            </h6>
            <button type="button" class="btn-close" data-action="dialog-close" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="small text-muted">
              Vous proposez un échange de données généalogiques avec un autre utilisateur.
              S'il accepte, son arbre sera enrichi du vôtre <strong>et</strong> votre arbre
              recevra le sien en retour.
            </p>
            <div class="mb-3">
              <label for="proposeEmailInput" class="form-label small fw-semibold">Email du destinataire</label>
              <input type="email" class="form-control" id="proposeEmailInput" placeholder="cousin@example.com" required>
            </div>
            <div class="mb-2">
              <label for="proposeMessageInput" class="form-label small fw-semibold">Message (optionnel)</label>
              <textarea class="form-control" id="proposeMessageInput" rows="3" maxlength="2000"
                placeholder="Un petit mot pour accompagner la proposition…"></textarea>
            </div>
            <div id="proposeResult" class="small mt-2" style="display:none;"></div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary btn-sm" data-action="dialog-close">Annuler</button>
            <button type="button" class="btn btn-primary btn-sm" id="proposeSubmitBtn" disabled>
              Proposer l'échange
              <span class="spinner-border spinner-border-sm ms-2" id="proposeSpinner" style="display:none;" role="status"></span>
            </button>
          </div>
      </div>`;

    // Backdrop click + explicit close buttons.
    modalEl.addEventListener('click', (e) => {
      if (e.target === modalEl) modalEl.close();
      else if (e.target.closest('[data-action="dialog-close"]')) modalEl.close();
    });

    const emailInput = modalEl.querySelector('#proposeEmailInput');
    const submitBtn = modalEl.querySelector('#proposeSubmitBtn');
    const resultEl = modalEl.querySelector('#proposeResult');
    const messageInput = modalEl.querySelector('#proposeMessageInput');
    const spinner = modalEl.querySelector('#proposeSpinner');

    const validate = () => {
      const v = (emailInput.value || '').trim().toLowerCase();
      submitBtn.disabled = !v || !v.includes('@') || v.length < 5;
    };
    emailInput.addEventListener('input', validate);

    submitBtn.addEventListener('click', async () => {
      const recipientEmail = (emailInput.value || '').trim().toLowerCase();
      const message = (messageInput.value || '').trim() || null;
      submitBtn.disabled = true;
      spinner.style.display = 'inline-block';
      resultEl.style.display = 'none';

      try {
        const res = await filesService.submitProposal({
          contributorFileId: fileId,
          recipientEmail,
          message,
        });
        const reusedSuffix = res?.reused ? ' (une proposition était déjà en cours)' : '';
        notificationManager.success(`Échange proposé à ${recipientEmail}${reusedSuffix}`, 4000);
        this._proposeModal?.close();
      } catch (err) {
        resultEl.innerHTML = `<div class="alert alert-danger py-2 mb-0">
          ❌ ${this._escapeHtml(err?.message || String(err))}
        </div>`;
        resultEl.style.display = 'block';
        submitBtn.disabled = false;
      } finally {
        spinner.style.display = 'none';
      }
    });

    return modalEl;
  }

  _cleanupProposeModal() {
    document.getElementById('gedcomProposeModal')?.remove();
    this._proposeModal = null;
  }

  _escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // =========================================
  // Delete
  // =========================================

  _disposeTooltips() {
    // No-op: tooltips inside the popover now use native `title`
    // attributes instead of Bootstrap Tooltip (which renders in the
    // DOM below the top-layer). Kept as a no-op for call-site compat.
  }

  async _handleDelete(fileId, actionEl) {
    const fileEntry = actionEl.closest('.gedcom-file-entry');
    const itemDiv = actionEl.closest('.gedcom-file-item');
    if (!fileEntry || !itemDiv) return;

    // Save original content for restoration
    const originalHtml = itemDiv.innerHTML;

    // Dispose tooltips before replacing content — prevents orphaned floating elements
    this._disposeTooltips(itemDiv);

    // Replace entire row with "Supprimer ? [Oui] [Non]"
    itemDiv.innerHTML = `
      <span class="text-danger fw-semibold">Supprimer ?</span>
      <span class="gedcom-confirm-buttons">
        <a href="#" class="btn btn-sm btn-outline-danger" data-action="confirm-delete" data-file-id="${fileId}">Oui</a>
        <a href="#" class="btn btn-sm btn-outline-secondary" data-action="cancel-delete">Non</a>
      </span>`;

    // Listen for confirm/cancel
    const handler = async (e2) => {
      const target = e2.target.closest('[data-action]');
      if (!target) return;

      e2.preventDefault();
      e2.stopPropagation();

      if (target.dataset.action === 'confirm-delete') {
        await this._executeDelete(fileId, fileEntry);
      } else if (target.dataset.action === 'cancel-delete') {
        itemDiv.innerHTML = originalHtml;
      }

      itemDiv.removeEventListener('click', handler);
    };

    itemDiv.addEventListener('click', handler);
  }

  async _executeDelete(fileId, fileEntryEl) {
    try {
      if (!authStore.isAuthenticated) throw new Error('User not authenticated');

      const { filesService } = await import('../services/filesService.js');
      const ok = await filesService.deleteFile(fileId);
      if (!ok) {
        throw new Error('Fichier introuvable ou non supprimable');
      }

      // Dispose any remaining tooltips before removing
      this._disposeTooltips(fileEntryEl);

      // Remove the entry from the dropdown with animation
      fileEntryEl.style.transition = 'opacity 0.3s';
      fileEntryEl.style.opacity = '0';
      setTimeout(() => fileEntryEl.remove(), 300);

      // Update files cache
      this._filesCache = this._filesCache?.filter(f => f.id !== fileId);

      // Show empty message if no files left
      if (!this._filesCache || this._filesCache.length === 0) {
        const emptyEl = document.getElementById('gedcom-files-empty');
        if (emptyEl) emptyEl.style.display = '';
      }
    } catch (error) {
      logger.gedcom.error('Error deleting file:', error);
    }
  }

  /**
   * Invalidate the files cache so next dropdown open reloads the list.
   * Call after uploading a new file.
   */
  invalidateCache() {
    this._filesCache = null;
  }
}

const gedcomDropdownManager = new GedcomDropdownManager();
export default gedcomDropdownManager;
